import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  MultiSelectorChangeParam,
  MultiSelectorOption,
  MultiSelectorSubOption
} from "./types";
import {
  Box,
  BoxProps,
  Center,
  Flex,
  FlexProps,
  Text,
  useColorModeValue
} from "@chakra-ui/react";
import { getRelativeRect } from "utils/relative-rect";

import Checkbox from "components/checkbox";
import { AnimateElement } from "utils/animations";

export function MultiSelector({
  options,
  switchToggleProps,
  containerProps,
  onChange,
  isActiveOption,
  isActiveSubOption,
  value,
  animationMaxTiming = 600,
  subOptionMultiSelect
}: {
  options: MultiSelectorOption[];
  switchToggleProps?: BoxProps;
  containerProps?: FlexProps;
  onChange: (option: MultiSelectorChangeParam) => void;
  isActiveOption?: (option: MultiSelectorOption) => boolean;
  isActiveSubOption?: (option: MultiSelectorSubOption) => boolean;
  value: any;
  animationMaxTiming?: number;
  subOptionMultiSelect?: boolean;
}) {
  const [isActiveSubOptions, setIsActiveSubOptions] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLDivElement>(null);
  const currentValueSetting = useRef<any>(null);
  const bg = useColorModeValue("secondaryGray.300", "#20263d");
  const bgToggle = useColorModeValue(
    "var(--chakra-colors-primary-500)",
    "var(--chakra-colors-primary-500)"
  );
  const border = useColorModeValue(
    "1px solid rgb(232, 234, 237)",
    "1px solid rgb(232, 234, 237)"
  );

  const getActiveOption = (v = value) =>
    options?.find((option) => option.value === v);

  const activeOption = getActiveOption();

  const {
    h = { base: "28px", "2xl": "32px" },
    height,
    ...styles
  } = containerProps || {};

  const crrHeight = h || height;
  const paddingX = 2;

  const activeIndex = options?.findIndex((option) =>
    isActiveOption ? isActiveOption(option) : option.value === value
  );

  const calcDistance = (target: HTMLDivElement) => {
    const toggle = toggleRef.current;
    const container = containerRef.current;
    if (toggle && container) {
      const toggleRect = getRelativeRect(toggle, container);
      const targetRect = getRelativeRect(target, container);

      const currX = toggleRect.left;
      const x = targetRect.left;
      const distance = Math.abs(toggleRect.left - targetRect.left);

      return { x, distance, currX };
    }
    return { x: 0, distance: 0, currX: 0 };
  };

  const adjustToggle = (target: HTMLDivElement, value?: any) => {
    const toggle = toggleRef.current;
    const container = containerRef.current;
    if (toggle && container && target) {
      const totalWidth = container.clientWidth - 2 * paddingX;
      const timingPerPixel = animationMaxTiming / totalWidth;

      let { distance, x, currX } = calcDistance(target);
      const duration = distance * timingPerPixel;

      const fromW = toggle.offsetWidth;
      const toW = target.offsetWidth;

      const fromT = currX;
      const toT = x;

      AnimateElement(duration, [fromT, toT], (progress) => {
        if (currentValueSetting.current === value) {
          toggle.style.transform = `translateX(${progress}px)`;
        }
      });

      AnimateElement(
        duration,
        [fromW, toW],
        (progress) => {
          if (currentValueSetting.current === value) {
            toggle.style.width = `${progress}px`;
          }
        },
        () => {
          if (
            currentValueSetting.current === value &&
            activeOption?.subOptions
          ) {
            setIsActiveSubOptions(true);
          }
        }
      );
    }
  };

  const setByIndex = (index: number, value?: any) => {
    if (!activeOption?.subOptions) setIsActiveSubOptions(false);
    currentValueSetting.current = value;
    const container = containerRef.current;
    if (container?.children.length) {
      let i = 0;
      let optionIndex = 0;
      for (let item of container.children) {
        if (i > 0) {
          if (optionIndex === index) {
            const currItem = item as HTMLDivElement;
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                adjustToggle(currItem, value);
              });
            });
          }
          optionIndex++;
        }
        i++;
      }
    }
  };

  const onChangeOption = (option: MultiSelectorOption) => {
    onChange(option);
  };

  useEffect(() => {
    setByIndex(activeIndex, value);
  }, [activeIndex]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => {
      setByIndex(activeIndex, value);
    });

    observer.observe(container);

    return () => observer.disconnect();
  }, [activeIndex]);

  return (
    <Flex
      h={crrHeight}
      px={`${paddingX}px`}
      borderRadius="10px"
      bg={bg}
      border={border}
      fontWeight="medium"
      fontSize="14px"
      {...styles}
    >
      {/* INSIDE CONTAINER */}
      <Flex
        ref={containerRef}
        h="100%"
        pos="relative"
        alignItems="center"
        zIndex={1}
      >
        {/* TOGGLE */}
        <Box
          ref={toggleRef}
          h={`calc(100% - 4px)`}
          pos="absolute"
          // zIndex="1"
          borderRadius="10px"
          bg={bgToggle}
          {...switchToggleProps}
        >
          {/* IF SUBOPTIONS CREATE A OPTIONS CONTAINER OR CUSTOM RENDER */}
          {isActiveSubOptions && activeOption?.subOptions ? (
            <Box pos="absolute" left="0" top="calc(100% + 10px)" zIndex={100}>
              <BalloonArrow
                bottom="auto"
                top="-5px"
                bg={bg}
                borderTop={border}
                borderLeft={border}
              />
              <Box
                bg={bg}
                minW="100%"
                border={border}
                borderRadius="10px"
                overflow={activeOption.containerRender ? "visible" : "auto"}
                maxH={"200px"}
              >
                {activeOption.containerRender?.() ??
                  activeOption.subOptions.map((item, i) => {
                    const isActive = isActiveSubOption?.(item);

                    return (
                      <Flex
                        key={i}
                        alignItems="center"
                        px="10px"
                        py="3px"
                        _hover={{ bg: "whiteAlpha.50" }}
                        onClick={() => {
                          onChange({ ...activeOption, subOption: item });
                        }}
                        whiteSpace="nowrap"
                        cursor="pointer"
                      >
                        {subOptionMultiSelect ? (
                          <Checkbox mr="6px" isChecked={isActive} />
                        ) : null}
                        <Text>{item.name}</Text>
                      </Flex>
                    );
                  })}
              </Box>
            </Box>
          ) : null}
        </Box>
        {/* OPTIONS */}
        {options.map((option, index) => {
          const isActive = isActiveOption?.(option) ?? option.value === value;
          return (
            <Center
              key={index}
              zIndex={1}
              px="8px"
              onClick={() => {
                onChangeOption(option);
              }}
              cursor="pointer"
              userSelect="none"
              color={isActive ? "#fff" : "var(--chakra-colors-primary-500)"}
              whiteSpace="nowrap"
              transition="color 0.3s ease"
            >
              {option.name}
            </Center>
          );
        })}
      </Flex>
    </Flex>
  );
}

export function BalloonArrow({ ...rest }: BoxProps) {
  return (
    <Box
      w="10px"
      h="10px"
      transform="rotate(45deg)"
      bottom="-13px"
      bg={"#fff"}
      left="30px"
      pos="absolute"
      borderTop={"1px solid #EAEAEA"}
      borderLeft={"1px solid #EAEAEA"}
      borderRadius="0 0 10px 0"
      zIndex={11}
      {...rest}
    />
  );
}
